import { createContext, ReactNode, useCallback, useContext, useMemo } from "react";
import { IChatMessage } from "../../types";
import { useTypedSelector } from "../../redux/hooks";
import { selectCurrentUserId } from "../../redux/slices";
import { useConversationContext } from "../ChatContext/contextConvo";
import { nebulaChatApi } from "../../redux/services/nebulaChat";

interface MessageContextValue {
    message: IChatMessage;
    myReactions: Record<string, string>;
    handleReaction: (reactionKey: string) => Promise<boolean> | undefined;
    reactionLoading: boolean;
}

const MessageContext = createContext<MessageContextValue | null>(null)

export const useMessageContext = () => {
    const context = useContext(MessageContext)

    if (!context?.message) {
        throw new Error(
            'Component requiring message context rendered without a message'
        )
    }

    return context
}

interface ProviderProps {
    children: ReactNode;
    message: IChatMessage;
}

const noopReaction = () => {
    console.error("Tried to send reaction from invalid comp");
    return undefined;
};

export const MessageProvider: React.FC<ProviderProps> = ({ children, message }) => (
    <MessageContext.Provider value={{ message, myReactions: {}, handleReaction: noopReaction, reactionLoading: false }}>
        {children}
    </MessageContext.Provider>
);

export const ConversationMessageProvider: React.FC<ProviderProps> = ({ children, message }) => {
    const userUuid = useTypedSelector(selectCurrentUserId);
    const { conversation } = useConversationContext();

    const [sendMessageReaction, { isLoading: reactionSendLoading }] = nebulaChatApi.endpoints.sendMessageReaction.useMutation();
    const [deleteMessageReaction, { isLoading: reactionDeleteLoading }] = nebulaChatApi.endpoints.deleteMessageReaction.useMutation();

    const myReactions: Record<string, string> = useMemo(() =>
        message?.reactions?.reduce((curr: Record<string, string>, reaction) => {
            if (reaction.user === userUuid) {
                return {
                    ...curr,
                    [reaction.content]: reaction.uuid
                };
            }
            return curr;
        }, {}) || {},
        [message?.reactions, userUuid]);

    const handleReaction = useCallback((reactionKey: string) => {
        if (reactionSendLoading || reactionDeleteLoading) return undefined;

        const uuid = myReactions[reactionKey];

        if (uuid) {
            return deleteMessageReaction({
                to: conversation.uuid,
                to_type: conversation.type,
                message_uuid: message.uuid,
                uuid,
            }).unwrap();
        }
        return sendMessageReaction({
            to: conversation.uuid,
            to_type: conversation.type,
            message_uuid: message.uuid,
            content: reactionKey,
        }).unwrap();
    }, [sendMessageReaction, deleteMessageReaction, reactionDeleteLoading, reactionSendLoading, conversation.uuid, conversation.type, message.uuid, myReactions]);

    return (
        <MessageContext.Provider value={{ message, myReactions, handleReaction, reactionLoading: reactionSendLoading || reactionDeleteLoading }}>
            {children}
        </MessageContext.Provider>
    )
}

export default MessageProvider
